import { useDelayedState } from '@mentimeter/react-hooks';
import type * as React from 'react';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Copy } from '@mentimeter/ragnar-ui/copy';
import { Text } from '@mentimeter/ragnar-ui/text';
import { ThemeBgSensitive } from '@mentimeter/ragnar-ui/themes';
import { useTranslations } from 'next-intl';
import Tooltip from '../../toolbar/Tooltip';
import { usePresentationSettings } from '../../data-mediation/series/usePresentationSettings';
import {
  useConfig,
  useQuestion,
  useTheme,
  useVoteCode,
} from '../../data-mediation';

const PORTAL_ID = 'join-instructions-bar';

export interface VoteCodeT {
  votingOpen: boolean;
  voteCode: string;
  url?: string;
}

export const VoteCodeWrapper = ({
  children,
  onClick,
  onMouseLeave,
  ...rest
}: React.PropsWithChildren<
  {
    onClick?: () => void;
    onMouseLeave?: () => void;
  } & BoxT
>) => {
  const translate = useTranslations('presenting.legacy');
  return (
    <Box
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      borderRadius="2xl"
      mt="space5"
      pr="space3"
      pl="space4"
      py="space3"
      id="votecode-header-tooltip"
      as="button"
      data-testid="vote-code-button"
      extend={({ theme }) => ({
        cursor: onClick ? 'pointer' : undefined,
        backdropFilter: theme.kosmosBlur.xl,
        WebkitBackdropFilter: theme.kosmosBlur.xl,
        backgroundColor: theme.colors.surfaceOverlayTransparent,
      })}
      aria-label={translate('voting_instructions.copy_participation_link')}
      {...rest}
    >
      {children}
    </Box>
  );
};

export const VoteCodeContent = ({
  voteCode,
  votingOpen,
  url,
  renderTooltip,
}: {
  votingOpen: boolean;
  voteCode?: string | undefined;
  url?: string;
  renderTooltip?: React.JSX.Element;
}) => {
  const translate = useTranslations('presenting.legacy');
  return (
    <>
      <Box id={PORTAL_ID} />
      {renderTooltip}
      <Text color="text">
        {!votingOpen || !voteCode ? (
          <Text
            color="text"
            fontWeight="regular"
            fontSize="175"
            lineHeight="snug"
          >
            {votingOpen && <>{translate('voting_instructions.join_at')} </>}
            <Text fontWeight="semiBold" fontSize="inherit" color="text">
              {!votingOpen ? translate('voting_instructions.closed') : `${url}`}
            </Text>
          </Text>
        ) : (
          <Text fontSize="175" color="text" lineHeight="snug">
            {/* Revert this once the voting_instructions.voting_closed_vote_code translation key is translated in enough languages. 
              We made this change to enable more translated languages to affect this code block, at the time it was only en, es, sv. */}
            {translate('voting_instructions.join_at')}{' '}
            <Text fontSize="inherit" color="text" lineHeight="inherit">
              menti.com
            </Text>{' '}
            | {translate('voting_instructions.use_code')}{' '}
            <Box
              px="space3"
              py="space1.5"
              display="inline-block"
              borderRadius="xl"
              bg="bg"
            >
              <Text
                fontWeight="semiBold"
                fontSize="inherit"
                lineHeight="inherit"
                color="text"
                data-testid="vote-code"
              >
                {voteCode}
              </Text>
            </Box>
          </Text>
        )}
      </Text>
    </>
  );
};

type CopyState = 'clickToCopy' | 'copied';

const VoteCode = () => {
  const voteCode = useVoteCode();
  const { votingUrl } = useConfig();
  const { participationKey, participationResponseMode } =
    usePresentationSettings();
  const { background_color } = useTheme();
  const { theme_settings } = useQuestion();
  const translate = useTranslations('presenting.legacy');
  const themeBg = theme_settings?.background_color ?? background_color;
  const url = `${votingUrl}/${participationKey}`;
  const [copyState, setCopyState] = useDelayedState<CopyState>('clickToCopy');

  const clickToCopyText = translate(
    'voting_instructions.click_to_copy_participation_link',
  );
  const copiedText = translate('voting_instructions.participation_link_copied');

  return (
    <ThemeBgSensitive themeBg={themeBg}>
      <Copy id="copy-votekey-url-button" src={url} showSuccessTooltip={false}>
        {({ copy }) => (
          <VoteCodeWrapper
            onClick={() => {
              setCopyState('copied');
              copy();
            }}
            onMouseLeave={() => setCopyState('clickToCopy', 500)}
          >
            <VoteCodeContent
              renderTooltip={
                <Tooltip
                  placement="right"
                  id="votecode-header-tooltip"
                  label={copyState === 'copied' ? copiedText : clickToCopyText}
                  portalId={PORTAL_ID}
                />
              }
              voteCode={voteCode}
              votingOpen={participationResponseMode === 'accepting-responses'}
              url={url}
            />
          </VoteCodeWrapper>
        )}
      </Copy>
    </ThemeBgSensitive>
  );
};
export default VoteCode;
